import React, { useEffect, useState } from "react";
import styled from "styled-components";
import logo from "../assets/logo.png";
import { Link } from "react-router-dom";
import DisableScrolling from "./DisableScrolling";
import BackButton from "./buttons/BackButton";
import { device } from "../styled";
import ReservePlaceButton from "./buttons/ReservePlaceButton";

const Container = styled.div`
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  overflow: hidden;

  @media ${device.desktop} {
    position: sticky;
    display: flex;
    align-items: center;
    justify-content: ${({ isPageScrolledDown }) =>
      isPageScrolledDown ? "center" : "space-between"};
    height: ${({ isPageScrolledDown }) =>
      isPageScrolledDown ? "60px" : "80px"};
    z-index: 2;
    background-color: rgba(0, 0, 0, 0.5);
    transition: all 0.3s ease-in-out;
  }
`;

const BurgerButton = styled.button`
  position: absolute;
  top: 25px;
  padding-top: 30px;
  right: 15px;
  z-index: 1000;
  background: none;
  border: 0;
  cursor: pointer;
  &:focus {
    outline: none;
  }

  @media ${device.desktop} {
    display: none;
  }
`;

const Burger = styled.span`
  box-shadow: #fff;
  position: relative;
  display: block;
  width: 28px;
  height: 4px;
  margin: 0 auto;
  background: #fff;
  transform: skew(5deg);
  transition: all 0.275s;
  &:after,
  &:before {
    content: "";
    display: block;
    height: 100%;
    background: #fff;
    transition: all 0.275s;
  }
  &:after {
    transform: translateY(-12px) translateX(-2px) skew(-20deg);
  }
  &:before {
    transform: translateY(-16px) skew(-10deg);
  }
  ${({ isOpen }) =>
    isOpen &&
    `
    transform: skew(5deg) translateY(-8px) rotate(-45deg);
    &:before {
      transform: translateY(0px) skew(-10deg) rotate(75deg);
    }
    &:after {
      transform: translateY(-12px) translateX(10px) skew(-20deg);
      opacity: 0;
    }
  `}
`;

const BurgerText = styled.span`
  display: block;
  font-size: 0.675rem;
  letter-spacing: 0.05em;
  margin-top: 0.5em;
  text-transform: uppercase;
  font-weight: 500;
  text-align: center;
  color: #fff;
`;

const MobileNav = styled.nav`
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  text-align: center;
  background: #fff;
  opacity: 0;
  z-index: -1;
  visibility: hidden;
  transition: all 0.375s;
  ${({ isOpen }) =>
    isOpen &&
    `
    opacity: 1;
    z-index: 100;
    visibility: visible;
  `}
  &::before {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: -15px;
    background: radial-gradient(
      circle,
      rgba(83, 83, 83, 1) 0%,
      rgba(68, 68, 68, 1) 39%,
      rgba(36, 36, 36, 1) 100%
    );
    transform-origin: 0 0;
    transform: skew(-14deg) translateX(-120%);
    transition: all 0.275s 0.1s;
    ${({ isOpen }) =>
      isOpen &&
      `
      transform: skew(-14deg) translateX(0);
    `}
  }
  ul {
    display: inline-flex;
    flex-direction: column;
    height: 93%;
    align-items: flex-end;
    justify-content: center;
    transform: translateX(-38%) skew(-14deg);
    list-style: none;
    padding: 0;
  }
  li {
    display: block;
    margin: 0.5rem 0;
    text-align: right;
    transform: skew(16deg);
  }
  a {
    display: block;
    padding: 12px 0;
    color: #fff;
    font-size: 1.4em;
    text-decoration: none;
    font-weight: bold;
    opacity: 0;
    transform: translateY(-10px);
    transition: all 275ms 0ms;
    &:hover {
      text-decoration: underline;
    }
  }
  ${({ isOpen }) =>
    isOpen &&
    `
    a, img {
      opacity: 1;
      transform: translateY(0);
    }
    li:nth-child(1) a { transition: all 275ms 175ms }
    li:nth-child(2) a { transition: all 275ms 225ms }
    li:nth-child(3) a { transition: all 275ms 275ms }
    li:nth-child(4) a { transition: all 275ms 325ms }
    li:nth-child(5) a { transition: all 275ms 375ms }
    li:nth-child(6) a { transition: all 275ms 325ms }
  `}
`;

const ImageContainer = styled.img`
  width: 50%;
`;

const LogoContainer = styled.img`
  position: absolute;
  padding-left: 20px;
  padding-top: 30px;
  width: ${({ isPageScrolledDown }) =>
    isPageScrolledDown ? "140px" : "170px"};
  z-index: 2;

  transition: all 0.5s ease;

  @media ${device.desktop} {
    padding-left: 60px;
    padding-top: 0;
    position: relative;
  }
`;

const ReserveButton = styled.div`
  border: 3px #008080 solid;
  border-radius: 10px;
  padding: 10px 5px;

  &:hover {
    background-color: #008080;
  }
`;

const DesktopNav = styled.div`
  display: flex;
  display: none;
  margin-right: 50px;

  ul {
    align-items: center;
    gap: 20px;
    display: ${({ isPageScrolledDown }) =>
      isPageScrolledDown ? "none" : "flex"};
    transition: all 0.5 ease-in-out;
  }
  li {
    list-style-type: none;
  }

  a {
    text-decoration: none;
    color: #fff;
    font-weight: 600;
  }

  @media ${device.desktop} {
    display: block;
  }
`;

function Navbar({ back, standAlone }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isPageScrolledDown, setIsPageScrolledDown] = useState(false);

  const toggleMenu = () => setIsOpen(!isOpen);

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.addEventListener("scroll", () =>
        setIsPageScrolledDown(window.pageYOffset > 200)
      );
    }
  }, []);

  return (
    <Container isPageScrolledDown={isPageScrolledDown}>
      {back ? (
        <BackButton standAlone={standAlone} src={"/back.png"} />
      ) : (
        <Link to="/">
          <LogoContainer isPageScrolledDown={isPageScrolledDown} src={logo} />
        </Link>
      )}
      <DisableScrolling isOpen={isOpen} />
      <BurgerButton onClick={toggleMenu}>
        <Burger isOpen={isOpen} />
        <BurgerText>Menu</BurgerText>
      </BurgerButton>
      <MobileNav isOpen={isOpen}>
        <ul>
          <li>
            <ImageContainer src={logo} />
          </li>
          <li>
            <Link to={"/connection"}>Kapcsolat</Link>
          </li>
          <li>
            <Link to={"/about-us"}>Rólunk</Link>
          </li>
          <li>
            <Link to={"/carrier"}>Karrier</Link>
          </li>
          <li>
            <Link to={"/gallery"}>Galéria</Link>
          </li>
          <li>
            <Link to={"/artist-barbershops"}>
              <ReservePlaceButton first="Időpontfoglalás" second="Királyság!" />
            </Link>
          </li>
        </ul>
      </MobileNav>
      <DesktopNav isPageScrolledDown={isPageScrolledDown}>
        <ul>
          <li>
            <Link to={"/connection"}>Kapcsolat</Link>
          </li>
          <li>
            <Link to={"/about-us"}>Rólunk</Link>
          </li>
          <li>
            <Link to={"/carrier"}>Karrier</Link>
          </li>
          <li>
            <Link to={"/gallery"}>Galéria</Link>
          </li>
          <li>
            <Link to={"/artist-barbershops"}>
              <ReserveButton>Időpontfoglalás</ReserveButton>
            </Link>
          </li>
        </ul>
      </DesktopNav>
    </Container>
  );
}

export default Navbar;
